import { useState, useEffect, useRef } from 'react';
import { X, Package, AlertTriangle, ShoppingCart, Edit2, Minus, Plus, Layers, MapPin, FileText, ExternalLink, Barcode, Maximize2, Camera, Trash2, Truck, QrCode } from 'lucide-react';
import { SmartVariantPicker } from '@/components/products/SmartVariantPicker';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import JsBarcode from 'jsbarcode';
import { formatCurrency } from '@/lib/utils';
import { productsApi, uploadApi, fileUrl } from '@/lib/api';
import api from '@/lib/api';
import { toast } from 'sonner';
import CategoryIcon from '@/components/common/CategoryIcon';

// ── Barcode popup ──────────────────────────────────────────────────────────
const VariantBarcodePopup = ({ variant, product, onClose }) => {
  const svgRef = useRef(null);
  const code = variant?.sku || variant?.reference || product?.barcode_value || product?.reference;

  useEffect(() => {
    if (!svgRef.current || !code) return;
    try {
      JsBarcode(svgRef.current, code, {
        format: 'CODE128',
        width: 2,
        height: 80,
        displayValue: true,
        fontSize: 14,
        margin: 10,
        background: '#ffffff',
        lineColor: '#111111',
      });
    } catch (e) {}
  }, [code]);

  if (!code) return null;

  const download = () => {
    const svg = svgRef.current;
    const data = new XMLSerializer().serializeToString(svg);
    const blob = new Blob([data], { type: 'image/svg+xml' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `barcode_${code}.svg`;
    a.click();
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div
        className="relative bg-slate-900 border border-slate-700 rounded-2xl p-6 flex flex-col items-center gap-4 shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <button onClick={onClose} className="absolute top-3 right-3 p-1 rounded-lg text-slate-500 hover:text-white">
          <X className="w-4 h-4" />
        </button>
        <p className="text-sm font-semibold text-slate-300">{variant?.label ?? product?.name}</p>
        <div className="bg-white rounded-xl p-4">
          <svg ref={svgRef} />
        </div>
        <button
          onClick={download}
          className="w-full py-2 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold transition-colors"
        >
          Descarregar SVG
        </button>
      </div>
    </div>
  );
};

// ── QR Code popup ─────────────────────────────────────────────────────────
// `qrData` = { url, ref, name }
// O QR codifica o URL do produto (abre directo no browser do telemóvel)
const QrCodePopup = ({ qrData, onClose }) => {
  const [dataUrl, setDataUrl] = useState('');
  const { url, ref, name } = qrData || {};

  useEffect(() => {
    if (!url) return;
    import('qrcode').then(QRCode => {
      QRCode.default.toDataURL(url, {
        width: 300, margin: 2,
        color: { dark: '#1B1D21', light: '#ffffff' },
        errorCorrectionLevel: 'M',
      }).then(setDataUrl);
    });
  }, [url]);

  const download = () => {
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = `qr_${ref || 'produto'}.png`;
    a.click();
  };

  const copyUrl = () => {
    navigator.clipboard.writeText(url).catch(() => {});
  };

  if (!url) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div className="relative bg-slate-900 border border-slate-700 rounded-2xl p-6 flex flex-col items-center gap-4 shadow-2xl w-72" onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 right-3 p-1 rounded-lg text-slate-500 hover:text-white">
          <X className="w-4 h-4" />
        </button>

        {/* Header */}
        <div className="text-center">
          <p className="text-sm font-semibold text-slate-200 leading-snug">{name}</p>
          {ref && <p className="text-xs font-mono text-slate-500 mt-0.5">{ref}</p>}
        </div>

        {/* QR */}
        <div className="bg-white rounded-2xl p-3 shadow-inner">
          {dataUrl
            ? <img src={dataUrl} alt="QR Code" className="w-52 h-52" />
            : <div className="w-52 h-52 bg-slate-100 rounded-xl skeleton-shimmer" />}
        </div>

        {/* Instrução */}
        <p className="text-[11px] text-slate-500 text-center leading-relaxed">
          Aponta a câmara do telemóvel para abrir este produto directamente no ERP
        </p>

        {/* Acções */}
        <div className="flex gap-2 w-full">
          <button
            onClick={copyUrl}
            className="flex-1 py-2 rounded-xl border border-slate-700 text-slate-400 hover:text-slate-200 text-xs font-medium transition-colors"
          >
            Copiar link
          </button>
          <button
            onClick={download}
            className="flex-1 py-2 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold transition-colors"
          >
            Descarregar PNG
          </button>
        </div>
      </div>
    </div>
  );
};

const DOC_TYPE_LABELS = {
  ficha_tecnica: 'Ficha técnica',
  manual: 'Manual',
  certificado: 'Certificado',
  outro: 'Outro',
};

const Row = ({ label, value, mono = false }) => value ? (
  <div className="flex items-start justify-between gap-4 py-2.5 border-b border-slate-800 last:border-0">
    <span className="text-xs text-slate-500 uppercase tracking-wide font-medium flex-shrink-0">{label}</span>
    <span className={`text-sm text-slate-300 text-right ${mono ? 'font-mono' : ''}`}>{value}</span>
  </div>
) : null;

const InlineBarcode = ({ code }) => {
  const svgRef = useRef(null);
  useEffect(() => {
    if (!svgRef.current || !code) return;
    try {
      JsBarcode(svgRef.current, code, {
        format: 'CODE128', width: 1.6, height: 50,
        displayValue: true, fontSize: 11, margin: 8,
        background: '#ffffff', lineColor: '#111111',
      });
    } catch {}
  }, [code]);
  if (!code) return null;
  return (
    <div className="rounded-xl border border-slate-800 bg-white p-3 flex justify-center">
      <svg ref={svgRef} />
    </div>
  );
};


const ProductDetailDrawer = ({
  product,
  categoryMap = {},
  open,
  onClose,
  onEdit,
  clientView = false,
  onAddToCart,
  locations = [],
  onStockChange,
}) => {
  const [quantity, setQuantity] = useState(1);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [barcodeVariant, setBarcodeVariant] = useState(null);
  const [qrPopupCode, setQrPopupCode] = useState(null);
  const [photoZoomed, setPhotoZoomed] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);
  useEffect(() => {
    if (open) {
      setQuantity(1);
      setSelectedVariant(null);
      setBarcodeVariant(null);
      setPhotoIndex(0);
    }
  }, [open, product?.id]);

  useEffect(() => { setPhotoIndex(0); }, [selectedVariant]);

  if (!product) return null;
  const cat = categoryMap[product.category];

  // All available photos: variant photo first if selected, then product photos array, then single photo_url
  const allPhotos = (() => {
    if (selectedVariant?.photo_url) return [selectedVariant.photo_url];
    const arr = product.photos?.length > 0 ? product.photos : (product.photo_url ? [product.photo_url] : []);
    return arr;
  })();
  const activePhoto = allPhotos[photoIndex] || null;
  const hasVariants = product.variants?.length > 0;

  // Effective price/stock: selected variant > product base
  const effectivePrice = selectedVariant?.price_override != null
    ? selectedVariant.price_override
    : product.selling_price;
  const effectiveStock = selectedVariant != null
    ? selectedVariant.stock
    : product.variants?.length > 0
      ? product.variants.reduce((s, v) => s + (v?.stock ?? 0), 0)
      : product.stock;
  // reservado = encomendas "Por enviar" ainda não entregues
  const effectiveReserved = selectedVariant != null
    ? (selectedVariant.reserved ?? 0)
    : product.variants?.length > 0
      ? product.variants.reduce((s, v) => s + (v?.reserved ?? 0), 0)
      : (product.reserved ?? 0);
  const effectiveCost = selectedVariant?.cost_price_override != null
    ? selectedVariant.cost_price_override
    : product.cost_price;
  const effectiveMargin = effectivePrice > 0
    ? ((effectivePrice - effectiveCost) / effectivePrice) * 100
    : 0;

  const outOfStock = effectiveStock <= 0;
  const needsVariant = hasVariants && !selectedVariant;

  const handleAdd = () => {
    onAddToCart?.(product, quantity, selectedVariant ?? null);
    onClose();
  };

  return (
    <>
      {/* Barcode popup */}
      {barcodeVariant && (
        <VariantBarcodePopup
          variant={barcodeVariant}
          product={product}
          onClose={() => setBarcodeVariant(null)}
        />
      )}
      {qrPopupCode && (
        <QrCodePopup
          qrData={qrPopupCode}
          onClose={() => setQrPopupCode(null)}
        />
      )}

      {/* Photo lightbox */}
      {photoZoomed && activePhoto && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/95 backdrop-blur-md"
          onClick={() => setPhotoZoomed(false)}
        >
          <button onClick={() => setPhotoZoomed(false)} className="absolute top-4 right-4 p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors">
            <X className="w-6 h-6" />
          </button>
          <img
            src={fileUrl(activePhoto)}
            alt={product.name}
            className="max-w-full max-h-full object-contain p-4"
            onClick={e => e.stopPropagation()}
          />
          {allPhotos.length > 1 && (
            <div className="absolute bottom-10 flex gap-2">
              {allPhotos.map((url, i) => (
                <button key={i} onClick={e => { e.stopPropagation(); setPhotoIndex(i); }}
                  className={`w-12 h-12 rounded-lg overflow-hidden border-2 transition-all ${i === photoIndex ? 'border-white' : 'border-white/30 opacity-50 hover:opacity-80'}`}>
                  <img src={fileUrl(url, 160)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
          <p className="absolute bottom-4 left-0 right-0 text-center text-white/40 text-xs">{product.name}</p>
        </div>
      )}

      {/* Overlay */}
      <div
        className={`fixed inset-0 z-40 bg-black/70 backdrop-blur-sm transition-opacity duration-300 ${open ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        onClick={onClose}
      />

      {/* Drawer */}
      <div className={`fixed right-0 top-0 h-full z-50 w-full max-w-md bg-slate-950 flex flex-col transition-transform duration-300 ease-out ${open ? 'translate-x-0' : 'translate-x-full'}`}>

        {/* Hero image */}
        <div className="relative flex-shrink-0 overflow-hidden" style={{ height: '52%' }}>
          {activePhoto ? (
            <img
              src={fileUrl(activePhoto, 800)}
              alt={product.name}
              className="w-full h-full object-cover transition-all duration-300 cursor-zoom-in"
              onClick={() => setPhotoZoomed(true)}
            />
          ) : (
            <div className="w-full h-full bg-slate-900 flex items-center justify-center">
              <CategoryIcon name={product.category} className="w-24 h-24 text-slate-700" strokeWidth={1} />
            </div>
          )}

          {/* Gradient bottom */}
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/30 to-transparent pointer-events-none" />

          {/* Zoom button — canto superior direito (abaixo do X) */}
          {activePhoto && (
            <button
              onClick={() => setPhotoZoomed(true)}
              className="absolute top-14 right-4 z-10 p-2 rounded-full bg-black/50 text-white/70 backdrop-blur-sm hover:text-white hover:bg-black/70 transition-colors"
              title="Ver foto em grande"
            >
              <Maximize2 className="w-4 h-4" />
            </button>
          )}

          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-10 p-2 rounded-full bg-black/50 text-white backdrop-blur-sm hover:bg-black/70 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Category badge */}
          {cat && (
            <div className="absolute top-4 left-4 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/50 backdrop-blur-sm">
              <CategoryIcon name={product.category} className="w-4 h-4 text-blue-300" />
              <span className="text-xs text-white/80 uppercase tracking-wide font-medium">{product.category}</span>
            </div>
          )}

          {/* Product name over gradient */}
          <div className="absolute bottom-0 left-0 right-0 p-5">
            <h2 className="text-2xl font-bold text-white font-['Outfit'] leading-tight drop-shadow-lg">
              {product.name}
            </h2>
            {(() => {
              const ref = selectedVariant?.sku || selectedVariant?.reference || product.reference || product.sku;
              return ref ? (
                <p className="text-sm text-slate-400 font-mono mt-1">
                  Ref: {ref}
                </p>
              ) : null;
            })()}
          </div>

          {/* Out of stock overlay */}
          {outOfStock && (
            <div className="absolute inset-0 bg-slate-900/80 flex items-center justify-center">
              <span className="text-red-400 font-bold text-2xl uppercase tracking-widest">Esgotado</span>
            </div>
          )}

        </div>

        {/* Thumbnail strip — abaixo do hero */}
        {allPhotos.length > 1 && (
          <div className="flex gap-1.5 px-3 py-2 bg-slate-950 border-b border-slate-800/60 flex-shrink-0 overflow-x-auto">
            {allPhotos.map((url, i) => (
              <button
                key={i}
                onClick={() => setPhotoIndex(i)}
                className={`w-12 h-12 rounded-lg overflow-hidden border-2 transition-all flex-shrink-0 ${i === photoIndex ? 'border-blue-500' : 'border-slate-700 opacity-50 hover:opacity-80'}`}
              >
                <img src={fileUrl(url, 160)} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}

        {/* Scroll content */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-5 space-y-3">

            {/* Variant picker */}
            {hasVariants && (() => {
              const isColorPicker = (product.variants || []).every(v => v.color);
              return (
              <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Layers className="w-3.5 h-3.5 text-slate-400" />
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider font-medium">
                    {isColorPicker ? 'Selecionar cor' : 'Selecionar tamanho / medida'}
                  </p>
                </div>
                <ErrorBoundary key={product.id}>
                  <SmartVariantPicker
                    key={product.id}
                    variants={product.variants}
                    selectedVariant={selectedVariant}
                    onSelect={setSelectedVariant}
                    onBarcode={setBarcodeVariant}
                  />
                </ErrorBoundary>
                {needsVariant && (
                  <p className="text-xs text-amber-400 mt-2">
                    {isColorPicker ? 'Seleciona uma cor para adicionar ao carrinho' : 'Seleciona um tamanho para adicionar ao carrinho'}
                  </p>
                )}
              </div>
              );
            })()}

            {/* Stock */}
            {(() => {
              const hasVehicles = locations.length > 0;
              const armazemStock = effectiveStock;
              const vehicleStocks = hasVehicles ? locations.map(l => ({ name: l.name, qty: ((selectedVariant ?? product).stock_locations || {})[l.name] ?? 0 })) : [];
              const totalStock = hasVehicles
                ? armazemStock + vehicleStocks.reduce((s, v) => s + v.qty, 0)
                : effectiveStock;

              return (
                <div className="rounded-xl border border-slate-800 bg-slate-900/60 px-4 py-3 space-y-2">
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider font-medium">Disponibilidade</p>
                  {hasVehicles ? (
                    <>
                      <div className="flex flex-wrap gap-2">
                        {/* Armazém chip */}
                        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-800 border border-slate-700">
                          <Package className="w-3 h-3 text-slate-400" />
                          <span className="text-xs text-slate-400">Arm.</span>
                          <span className={`text-xs font-bold tabular-nums ${armazemStock <= 0 ? 'text-red-400' : armazemStock <= 5 ? 'text-amber-400' : 'text-emerald-300'}`}>
                            {armazemStock} un.
                          </span>
                        </div>
                        {/* Vehicle chips */}
                        {vehicleStocks.map(v => (
                          <div key={v.name} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-800 border border-slate-700">
                            <Truck className="w-3 h-3 text-slate-400" />
                            <span className="text-xs text-slate-400 font-mono">{v.name}</span>
                            <span className={`text-xs font-bold tabular-nums ${v.qty <= 0 ? 'text-slate-600' : v.qty <= 5 ? 'text-amber-400' : 'text-emerald-300'}`}>
                              {v.qty} un.
                            </span>
                          </div>
                        ))}
                      </div>
                      <div className="pt-1">
                        <span className="text-xs text-slate-500">Total: <span className="font-bold text-slate-300">{totalStock} un.</span></span>
                      </div>
                    </>
                  ) : (
                    <div className="flex items-center gap-4">
                      <div className="flex-1">
                        <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden">
                          {effectiveStock > 0 && (
                            <div className={`h-full rounded-full ${effectiveStock <= 5 ? 'bg-amber-400' : 'bg-emerald-400'}`}
                              style={{ width: `${Math.min((effectiveStock / 20) * 100, 100)}%` }} />
                          )}
                        </div>
                      </div>
                      <span className={`text-sm font-bold tabular-nums flex-shrink-0 ${outOfStock ? 'text-red-400' : effectiveStock <= 5 ? 'text-amber-400' : 'text-emerald-400'}`}>
                        {outOfStock ? 'Esgotado' : `${effectiveStock} un.`}
                      </span>
                    </div>
                  )}
                  {effectiveReserved > 0 && (
                    <div className="grid grid-cols-3 gap-2 border-t border-slate-800 pt-2 text-center tabular-nums">
                      <div>
                        <p className="text-[10px] uppercase tracking-wider text-slate-500">Armazém</p>
                        <p className="font-display text-lg font-bold text-slate-200">{effectiveStock}</p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-wider text-slate-500">Reservado</p>
                        <p className="font-display text-lg font-bold text-amber-300">{effectiveReserved}</p>
                      </div>
                      <div>
                        <p className="text-[10px] uppercase tracking-wider text-slate-500">Disponível</p>
                        <p className={`font-display text-lg font-bold ${effectiveStock - effectiveReserved > 0 ? 'text-emerald-300' : 'text-red-400'}`}>
                          {effectiveStock - effectiveReserved}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Price */}
            <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-4">
              <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-2 font-medium">Preço de venda</p>
              <p className="text-4xl font-bold text-slate-50 font-['Outfit'] tracking-tight">
                {formatCurrency(effectivePrice)}
              </p>
              {!clientView && effectiveCost > 0 && (
                <div className="flex items-center gap-2 mt-2 pt-2 border-t border-slate-800">
                  <span className="text-xs text-slate-500">Custo: {formatCurrency(effectiveCost)}</span>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                    effectiveMargin < 15
                      ? 'text-amber-400 bg-amber-400/10'
                      : 'text-emerald-400 bg-emerald-400/10'
                  }`}>
                    {effectiveMargin.toFixed(1)}% margem
                  </span>
                  {effectiveMargin < 15 && <AlertTriangle className="w-3 h-3 text-amber-400" />}
                </div>
              )}
            </div>

            {/* Description */}
            {product.description && (
              <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-4">
                <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-2 font-medium">Descrição</p>
                <p className="text-sm text-slate-300 leading-relaxed">{product.description}</p>
              </div>
            )}

            {/* Details table */}
            <div className="rounded-xl border border-slate-800 overflow-hidden">
              <div className="px-4">
                <Row label="Referência"    value={selectedVariant?.sku || selectedVariant?.reference || product.reference} mono />
                {!selectedVariant && <Row label="SKU" value={product.sku !== product.reference ? product.sku : null} mono />}
                <Row label="Categoria"     value={product.category} />
                {!clientView && <Row label="Fornecedor" value={product.supplier_name} />}
              </div>
            </div>

            {/* Barcode + QR */}
            {(() => {
              const code = selectedVariant?.sku || selectedVariant?.reference || product.barcode_value || product.reference;
              const productUrl = `${window.location.origin}/produtos?open=${product.id}`;
              return (
                <div className="flex flex-col gap-2">
                  {code && <InlineBarcode code={code} />}
                  <button
                    onClick={() => setQrPopupCode({
                      url: productUrl,
                      ref: code || product.reference,
                      name: selectedVariant?.label ? `${product.name} · ${selectedVariant.label}` : product.name,
                    })}
                    className="flex items-center justify-center gap-2 mx-4 py-2 rounded-xl border border-slate-700 text-slate-400 hover:border-blue-500/60 hover:text-blue-400 text-xs font-medium transition-colors"
                  >
                    <QrCode className="w-3.5 h-3.5" />
                    QR Code — abrir no telemóvel
                  </button>
                </div>
              );
            })()}

            {/* Warehouse location */}
            {product.warehouse_location && (
              <div className="flex items-center gap-3 rounded-xl border border-slate-800 bg-slate-900/60 px-4 py-3">
                <MapPin className="w-4 h-4 text-slate-400 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider font-medium">Localização em armazém</p>
                  <p className="text-sm text-slate-200 font-mono mt-0.5">{product.warehouse_location}</p>
                </div>
              </div>
            )}

            {/* Technical documents */}
            {product.documents?.length > 0 && (
              <div className="rounded-xl border border-slate-800 bg-slate-900/60 overflow-hidden">
                <div className="flex items-center gap-2 px-4 pt-3 pb-2">
                  <FileText className="w-3.5 h-3.5 text-slate-400" />
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider font-medium">Documentos Técnicos</p>
                </div>
                <div className="divide-y divide-slate-800">
                  {product.documents.map(doc => (
                    <a
                      key={doc.id}
                      href={doc.url}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-800/50 transition-colors group"
                    >
                      <FileText className="w-4 h-4 text-blue-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-slate-200 truncate">{doc.name}</p>
                        {doc.doc_type && (
                          <p className="text-[11px] text-slate-500">{DOC_TYPE_LABELS[doc.doc_type] ?? doc.doc_type}</p>
                        )}
                      </div>
                      <ExternalLink className="w-3.5 h-3.5 text-slate-600 group-hover:text-blue-400 transition-colors flex-shrink-0" />
                    </a>
                  ))}
                </div>
              </div>
            )}

          </div>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 border-t border-slate-800 bg-slate-950 p-4 space-y-2">
          <div className="flex items-center gap-3">
          {/* Edit button */}
          {onEdit && (
            <button
              onClick={() => { onEdit(product); onClose(); }}
              className="w-10 h-10 flex items-center justify-center rounded-xl border border-slate-700 bg-slate-800/60 text-slate-400 hover:text-white hover:border-slate-500 transition-colors flex-shrink-0"
              title="Editar produto"
            >
              <Edit2 className="w-4 h-4" />
            </button>
          )}
          {/* Quantity stepper */}
          <div className="flex items-center rounded-xl border border-slate-700 bg-slate-800/60 overflow-hidden">
            <button
              onClick={() => setQuantity(q => Math.max(1, q - 1))}
              className="w-10 h-10 flex items-center justify-center text-slate-400 hover:text-white hover:bg-slate-700/50 transition-colors"
            >
              <Minus className="w-4 h-4" />
            </button>
            <span className="w-9 text-center text-sm font-bold text-slate-100">{quantity}</span>
            <button
              onClick={() => setQuantity(q => q + 1)}
              disabled={outOfStock}
              className="w-10 h-10 flex items-center justify-center text-slate-400 hover:text-white hover:bg-slate-700/50 transition-colors disabled:opacity-30"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>

          {/* Add to cart */}
          <button
            disabled={outOfStock || needsVariant}
            onClick={onAddToCart ? handleAdd : onClose}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 active:bg-blue-700 text-white font-semibold text-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ShoppingCart className="w-4 h-4" />
            {outOfStock ? 'Sem stock' : needsVariant ? ((product.variants || []).every(v => v.color) ? 'Escolhe uma cor' : 'Escolhe um tamanho') : 'Adicionar à encomenda'}
          </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProductDetailDrawer;
